import { useState, useEffect, useCallback, useRef } from 'react';

import { getLogger } from '@/lib/logger/logger';

const logger = getLogger('useDeviceDetect');

// 브레이크포인트 정의 (Tailwind CSS 기준)
const BREAKPOINTS = {
  mobile: 1280, // xl (헤더의 xl: 접두사와 일치)
  desktop: 1280, // xl
} as const;

export type DeviceType = 'mobile' | 'desktop';

interface UseDeviceDetectReturn {
  deviceType: DeviceType;
  isMobile: boolean;
  isDesktop: boolean;
  screenWidth: number;
}

export const useDeviceDetect = (): UseDeviceDetectReturn => {
  const [screenWidth, setScreenWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );

  const getDeviceType = useCallback((width: number): DeviceType => {
    if (width < BREAKPOINTS.mobile) return 'mobile';
    return 'desktop';
  }, []);

  const [deviceType, setDeviceType] = useState<DeviceType>(() => getDeviceType(screenWidth));

  // Refs to track previous values for comparison (avoids nested functional updates)
  const prevWidthRef = useRef<number>(screenWidth);
  const prevDeviceTypeRef = useRef<DeviceType>(deviceType);

  // ✅ P0-1: Fixed infinite loop by removing deviceType and screenWidth from dependencies
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;

      if (prevWidth === newWidth) return;

      const newDeviceType = getDeviceType(newWidth);
      const prevDeviceType = prevDeviceTypeRef.current;

      if (prevDeviceType !== newDeviceType) {
        logger.info('Device type changed', {
          from: prevDeviceType,
          to: newDeviceType,
          width: newWidth,
        });
      }

      prevWidthRef.current = newWidth;
      prevDeviceTypeRef.current = newDeviceType;
      setScreenWidth(newWidth);
      setDeviceType(newDeviceType);
    };

    // 초기 로깅
    logger.debug('Device detection initialized', {
      deviceType: getDeviceType(window.innerWidth),
      screenWidth: window.innerWidth,
      breakpoints: BREAKPOINTS,
    });

    // 리사이즈 이벤트 리스너 등록
    window.addEventListener('resize', handleResize);

    // 초기 실행
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getDeviceType]); // Only depend on getDeviceType (stable due to useCallback)

  return {
    deviceType,
    isMobile: deviceType === 'mobile',
    isDesktop: deviceType === 'desktop',
    screenWidth,
  };
};
